import { Component, h, Element, Host, State, Method, Prop, Event, EventEmitter, Watch } from '@stencil/core';
import { TiMetricsActionEventDetail, TiResponsive, TiResponsiveHandler, TiResponsiveViewport } from '@ticom/web-component-utils';
import { ColumnSortEventDetail } from '@ticom/ui-components/dist';

/**
 * Techdocs is used to display technical documentation and provide sorting and filtering features.
 *
 * @slot unnamed - Use for the TABLE element containing the techdocs items.
 */
@Component({
    tag: 'ti-techdocs',
    styleUrl: 'techdocs.scss',
    shadow: true
})
export class Techdocs implements TiResponsive {
    /**
     * Column widths set in colgroup
     */
    private _columnWidths = [];

    /**
     * Internal index to track date column - could be different based on language
     */
    private _dateColumnIndex: number;

    /**
     * Internal index to track pdfhtml column - could be different based on language
     */
    private _pdfhtmlColumnIndex: number;

    /**
     * Internal index to track pdfhtml column - could be different based on language
     */
    private _pdfhtmlTransColumnIndex: number;

    /**
     * Internal index to track title column - could be different based on language
     */
    private _titleColumnIndex: number;

    /**
     * Internal index to track doc type column - could be different based on language
     */
    private _typeColumnIndex: number;

    /**
     * Text area element used for decoding html content. Cached for reuse.
     *
     * @private
     * @type {HTMLTextAreaElement}
     * @memberof Techdocs
     */
    private _decodeElement: HTMLTextAreaElement;

    /**
     * The document type filter that is active.
     * This will be initialized to the `docTypeAllValue` value.
     */
    private _docTypeFilter: string;

    /**
     * Comma separated list of doc type labels used for the doc type select control.
     */
    private _docTypeLabels: string = '';

    /**
     * Comma separated list of doc type values used for the doc type select control.
     */
    private _docTypeValues: string = '';

    /**
     * Keys to identify columns. Always set in English, regardless of header label language
     */
    private _headerKeyNames = [];

    /**
     * Internal copy of table headers that were slotted.
     */
    private _headers = [];

    /**
     * Internal copy of row items that were slotted
     */
    private _items = [];

    /**
    * Internal copy of nested row items
    */
    private _nestedItems = {};

    /**
     * Max. number of rows to show without "view all"
     */
    private _rowsToShowDefault = 15;

    /**
     * Number of rows to show
     */
    private _rowsToShow = this._rowsToShowDefault;


    /**
     * flag to determine if all rows should be shown (without "view all")
     */
    private _showAll = false;

    /**
     * table ready status
     */
    private _statusType = {
        LOADING: 'LOADING',
        READY: 'READY',
        ERROR: 'ERROR'
    }


    /**
     * The document title filter that is active.
     */
    private _titleFilter = '';

    @Element() hostElement: HTMLElement;

    /**
     * Model that represents the row items that we display
     */
    @State() items = [];

    /**
     * Keep track of the status of the component so that we can render
     * a response to the user accordingly.
     *
     * @type {string}
     * @memberof Techdocs
     */
    @State() status: string = this._statusType.LOADING;

    /**
     * The current viewport size tracked by TiResponsiveHandler
     *
     * @type {TiResponsiveViewport}
     * @memberof Techdocs
     */
    @TiResponsiveHandler()
    @State() viewportSize: TiResponsiveViewport;
    @Watch('viewportSize')
    handleViewportSizeChange() {
        // track changes to viewportSize and reset all filters when a new breakpoint is triggered
        this._resetFilters();
    }

    /**
     * Label used for the 'All' doc type filter option.
     *
     * @type {string}
     * @memberof Techdocs
     */
    @Prop() docTypeAllLabel: string = 'All';

    /**
     * Value used for the 'All' doc type filter option. If unspecified, the `doc-type-all-label` will be used
     *
     * @type {string}
     * @memberof Techdocs
     */
    @Prop({ mutable: true }) docTypeAllValue: string = 'All';
    @Watch('docTypeAllValue')
    handleDocTypeAllValueChange(newValue: string, oldValue: string) {
        // update the filter if it is set to the 'All' value
        if (this._docTypeFilter === oldValue) {
            this._docTypeFilter = newValue;
        }
    }

    /**
     * Event name prefix for metrics/analytics. Typically one of:
     *
     *     `'folder_product'`
     *     `'folder_tool'`
     * .
     * @type {string}
     * @memberof Techdocs
     */
    @Prop() metricsName: string = '';

    /**
     * Placeholder text for the doc title filter search field.
     *
     * @type {string}
     * @memberof Techdocs
     */
    @Prop() searchPlaceholder: string;

    /**
     * Event fired when document type filter changes.
     *
     * @type {EventEmitter<TiMetricsActionEventDetail>}
     * @memberof Techdocs
     */
    @Event() tiMetricsAction: EventEmitter<TiMetricsActionEventDetail>;

    componentWillLoad() {
        if (this._items && this._items.length > 0) return;
        if (this.status !== this._statusType.LOADING) return;
        if (this.hostElement.children.length === 0) {
            this.status = this._statusType.ERROR;
            return;
        }
        // find the table!
        const table = this.hostElement.querySelector('table');
        if (!table) {
            this.status = this._statusType.ERROR;
            return;
        }

        // set initial doc type filter selection to all
        this._docTypeFilter = this.docTypeAllValue;

        // create the decode text area element
        this._decodeElement = document.createElement('textarea');

        // get colgroup columns and their widths
        const columnElements = table.querySelectorAll('col');
        columnElements.forEach(columnElement => {
            const columnWidth = columnElement.getAttribute('prop-width');
            if (columnWidth === null) {
                this._columnWidths.push('auto');
            } else {
                this._columnWidths.push(columnWidth);
            }
        });

        const headerElements = table.querySelectorAll('thead > tr > th');
        // get the headers first so that we have fields
        headerElements.forEach((headerElement, colNum) => {
            let headerName = headerElement.innerHTML.trim();
            const headerKey = headerElement.getAttribute('prop-key');
            if (colNum === 0) {
                headerName = ' ';
            }
            this._headers.push(headerName);

            // featured not passing through, so doing something similar to the headers
            this._headerKeyNames.push(headerKey !== null ? headerKey : ' ');
            //check for column index of date
            switch (headerKey) {
                case 'date':
                    this._dateColumnIndex = colNum;
                    break;
                case 'pdf-html-trans':
                    this._pdfhtmlTransColumnIndex = colNum;
                    break;
                case 'pdf-html':
                    this._pdfhtmlColumnIndex = colNum;
                    break;
                case 'title':
                    this._titleColumnIndex = colNum;
                    break;
                case 'type':
                    this._typeColumnIndex = colNum;
                    break;

            }
        });

        // process the rest and stash in given column
        const docTypes = {};
        const bodyRows = table.querySelectorAll('tbody > tr');
        this._items = Array.from(bodyRows, rowElement => {
            const row = {};
            for (let colNum = 0; colNum < rowElement.children.length; colNum++) {
                const cellElement = rowElement.children.item(colNum) as HTMLElement;
                const cellContent = cellElement.innerHTML.trim();
                if (colNum === 0) {
                    row[this._headerKeyNames[colNum]] = { featured: ('*' === cellContent) };
                } else if (colNum === this._typeColumnIndex) {
                    const docTypeValue = cellElement.getAttribute('doc-type') || cellContent;
                    docTypes[cellContent] = docTypeValue;
                    row[this._headerKeyNames[colNum]] = {
                        label: this._decodeEntity(cellContent),
                        value: docTypeValue
                    };
                } else if (colNum === this._pdfhtmlColumnIndex || colNum === this._pdfhtmlTransColumnIndex) {
                    const anchorElement = cellElement.querySelectorAll('a, span');
                    anchorElement.forEach((elementTag) => {
                        if (anchorElement) {
                            this._nestedItems[elementTag.innerHTML] = {
                                href: elementTag.getAttribute('href'),
                                name: elementTag.getAttribute('name'),
                                dataNavtitle: elementTag.getAttribute('data-navtitle') || '',
                                innerText: elementTag.innerHTML,
                                target: elementTag.getAttribute('target')
                            };
                        }
                    });
                    row[this._headerKeyNames[colNum]] = this._nestedItems;
                    // reset dictionary after each iteration
                    this._nestedItems = {};
                }
                else if (colNum === this._dateColumnIndex) {
                    row[this._headerKeyNames[colNum]] = {
                        label: this._decodeEntity(cellContent),
                        value: cellElement.getAttribute('utc-date')
                    };
                } else {
                    // title or english version column
                    const anchorElement = cellElement.querySelector('a');
                    if (anchorElement) {
                        row[this._headerKeyNames[colNum]] = {
                            href: anchorElement.getAttribute('href'),
                            name: anchorElement.getAttribute('name'),
                            dataNavtitle: anchorElement.getAttribute('data-navtitle') || '',
                            innerText: anchorElement.innerText,
                            target: anchorElement.getAttribute('target')
                        };
                    } else {
                        // english version column may not have an anchor element
                        row[this._headerKeyNames[colNum]] = this._decodeEntity(cellContent);
                    }
                }
            }
            return row;
        });
        // create the doc type labels and values for the select element
        const docTypeLabels = Object.keys(docTypes).sort();
        const docTypeValues = docTypeLabels.map(label => docTypes[label]);
        this._docTypeLabels = docTypeLabels.join(',');
        this._docTypeValues = docTypeValues.join(',');

        // copy _items to items so that we display something
        this.items = this._items.slice(0, this._rowsToShow);
        this._dataTechdocCount = this.items.length;
        this._dataTechdocFilter = this._docTypeFilter + '|' + this._titleFilter;

        // can't remove our slotted table from the document,
        // so we'll just hide it instead?
        table.setAttribute('style', 'display: none');

        // update our status to start showing stuff
        this.status = this._statusType.READY;
    }

    componentWillUpdate() {
        this._dataTechdocCount = this.items.length;
        this._dataTechdocFilter = this._docTypeFilter + '|' + this._titleFilter;
    }

    disconnectedCallback() {
        // required by TiResponsive
    }

    /**
     * Way to get the columns of the table
     * @param index
     */
    @Method()
    async getColumn(index:number) {
        return this.items.map(item => item[this._headerKeyNames[index]]);
    }

    /**
     * Way to get the table headers.
     * Columns that didn't have a header title will be named after
     * their column index. eg: "column0"
     */
    @Method()
    async getHeaders() {
        return this._headers.slice();
    }

    /**
     * The number of rows that are currently shown.
     * Used by GA to track filtering
     * @type {number}
     */
    private _dataTechdocCount: number;

    /**
     * The document type and title filters currently applied.
     * Used by GA to track filtering
     * @type {string}
     */
    private _dataTechdocFilter: string;

    private _decodeEntity(encodedStr) {
        this._decodeElement.innerHTML = encodedStr;
        return this._decodeElement.value;
    }

    private _filter() {
        const docTypeFilter = this._docTypeFilter;
        const titleFilter = this._titleFilter;

        // both filters not in use
        if (docTypeFilter === this.docTypeAllValue && titleFilter === '') {
            if (!this._showAll) {
                this._rowsToShow = this._rowsToShowDefault;
            }
            return this._items.slice(0, this._rowsToShow);
        }

        this._rowsToShow = this._items.length;
        // lets filter
        const newItems = this._items.filter(item =>
            this._isMatchDocType(docTypeFilter, item.type.value) && this._isMatchTitle(titleFilter, item.title)
        );

        return newItems.slice(0, this._rowsToShow);
    }

    private _isMatchDocType(filter, val) {
        return (filter === this.docTypeAllValue || filter === val);
    }

    private _isMatchTitle(filter, item) {
        return (filter === '' || item.innerText.toLowerCase().indexOf(filter) > -1);
    }

    private _renderRowItem(item, headerKey) {
        if (typeof item !== 'object') {
            return <td class="ti-techdocs-table-cell-nowrap">{item}</td>;
        }

        if (item.featured != undefined) {
            return item.featured
                ? <td><ti-svg-icon appearance="tertiary" size="s">star</ti-svg-icon></td>
                : <td></td>;
        }

        // NOTE: data-navtitle is found on link to English version of CN or JP document
        const additionalProps = item.dataNavtitle
            ? { 'data-navtitle': item.dataNavtitle }
            : {};
        // if the original targeted something else, we should too
        if (item.target) {
            additionalProps['target'] = item.target;
        }
        additionalProps['name'] = item.name;

        if (headerKey === 'pdf-html' || headerKey ==='pdf-html-trans') {
            if (Object.keys(item).length === 0) {
                return <td><span>&nbsp;</span></td>;
            }
            return <td>
                <a href={item['PDF'].href.toLowerCase()} target={'_blank'} data-navtitle={item['PDF'].dataNavtitle}>{item['PDF'].innerText}</a>
                <span class="ti-techdocs-pdf-html-separator">|</span>
                <a href={item['HTML'].href.toLowerCase()} target={'_blank'} data-navtitle={item['HTML'].dataNavtitle}>{item['HTML'].innerText}</a>
            </td>;
        }
        else {
            if (item.href) {

                if (headerKey === 'title') {
                    if (this._titleFilter === '') {
                        return <td><a href={item.href} {...additionalProps}>{item.innerText}</a></td>;
                    }

                    if (item.innerText.toLowerCase().indexOf(this._titleFilter) > -1) {
                        const termLengh = this._titleFilter.length;
                        const termIndex = item.innerText.toLowerCase().indexOf(this._titleFilter);
                        const preTermString = item.innerText.substr(0, termIndex);
                        const caseSensitiveTerm = item.innerText.substr(termIndex, termLengh);
                        const postTermString = item.innerText.substr(termIndex + termLengh);
                        return <td><a href={item.href} {...additionalProps}>
                            {preTermString}
                            <span class="ti-techdocs-table-text-highlight">{caseSensitiveTerm}</span>
                            {postTermString}
                        </a></td>;
                    }
                }

                if (headerKey === 'download-english') {
                    return <td><a href={item.href} {...additionalProps}>{item.innerText}</a></td>;
                }
            }
        }
        return <td class="ti-techdocs-table-cell-nowrap">{item.label}</td>;
    }

    private _resetFilters() {
        // reset text filter
        this._titleFilter = '';

        // reset select filter
        this._docTypeFilter = this.docTypeAllValue;

        // apply reset filters
        this.items = this._filter();
    }

    /**
     * Handle changes to the doc type filter select.
     *
     * @param detail CustomEvent detail containing 'value' property which is the filter value
     */
    private _docTypeFilterChanged(detail: { value: string }) {
        this._docTypeFilter = detail.value;
        this.items = this._filter();

        /**
         * Emit tiMetricsAction event on change of selected document type
         */
        this.tiMetricsAction.emit({
            elementName: this.hostElement.tagName,
            eventName: 'filter',
            data: {
                filterColumn: 'document type filter',
                filterValue: this._docTypeFilter
            }
        });

    }

    private _showAllParts() {
        this._showAll = true;
        this._rowsToShow = this._items.length;
        this.items = this._filter();
        this._techdocsMetricsHandler('Show All');
    }

    private _sort(detail: ColumnSortEventDetail) {
        this._items.sort(this._sortDate(detail.sortDirection === 'up' ? 1 : -1));
        this.items = this._filter();
        this._techdocsMetricsHandler('Date Sort');
    }

    private _sortDate(order: number) {
        return function (a, b) {
            if (a.date.value < b.date.value) {
                return -(order);
            }
            if (a.date.value > b.date.value) {
                return order;
            }
            return 0;
        };
    }

    private _techdocsMetricsHandler(event: string) {
        try {
            // build metrics params
            // track and prefix for event always the same for tech docs
            const trackName = 'Link Track';
            const eventName = `${this.metricsName}_technical_documents|${event}`;

            // to avoid build fail, make sure function and vars exist first
            const metricsFunctionExists = (window['_tiAnalyticsTrack'] && typeof window['_tiAnalyticsTrack'] === 'function');
            const metricsVarsExist = (window['tiPageName'] && window['tiContentGroup']);

            if (metricsFunctionExists && metricsVarsExist) {
                window['_tiAnalyticsTrack'](trackName, eventName, window['tiPageName'], window['tiContentGroup']);
            }
        } catch (e) {
            // nothing to do
        }
    }

    /**
     * Handle changes to the title filter search field.
     *
     * @param detail CustomEvent detail containing 'eventType', 'reverted', and 'value' properties.
     */
    private _titleFilterChanged(detail: { eventType: string, reverted: string, value: string }) {
        const filterValue = detail.value.trim().toLowerCase();
        if (filterValue.length > 1) {
            this._titleFilter = filterValue;
        } else {
            this._titleFilter = '';
        }
        this.items = this._filter();

        // only track on first character when typing
        if (!detail.reverted && filterValue.length === 1) {
            this._techdocsMetricsHandler('Title Filter Interaction');
        }

        // and also track clear button
        // event type is click and nothing in value
        if (detail.eventType === 'click' && filterValue.length === 0) {
            this._techdocsMetricsHandler('Clear filter');
        }
    }

    render() {
        if (this.status == this._statusType.LOADING) {
            return [
                <ti-progress></ti-progress>,
                <div id="table-data"><slot /></div>
            ];
        } else if (this.status == this._statusType.READY) {
            return (
                <Host
                    class={
                        `${this.items.length === 0 ? 'ti-techdocs-empty' : ''}
                         ${this._rowsToShow === this._items.length || this.items.length === 0 || this.items.length < this._rowsToShow ? 'ti-techdocs-all' : ''}
                         ti-techdocs-${this.viewportSize}`
                    }
                    data-techdoc-count={this._dataTechdocCount}
                    data-techdoc-filter={this._dataTechdocFilter}
                >
                    <table>
                        <colgroup>
                            {this._columnWidths.map((value, index) =>
                                <col style={{ width: value, minWidth: value }} class={'ti-techdocs-table-column-' + (index + 1)} />
                            )}
                        </colgroup>
                        <thead>
                            <tr>
                                {this._headers.map((item, index) =>
                                    <th class="ti-techdocs-table-cell-nowrap">
                                        {index === this._dateColumnIndex
                                            ? <ti-column-sort-header sort-column={index} first-sort-direction="down" onTiColumnSortChange={ev => this._sort(ev.detail)}>{item}</ti-column-sort-header>
                                            : <div class='ti-techdocs-table-header-label'>{item}</div>
                                        }
                                        <div>
                                            {index === this._typeColumnIndex &&
                                                <ti-select
                                                    labels={`${this.docTypeAllLabel},${this._docTypeLabels}`}
                                                    options={`${this.docTypeAllValue},${this._docTypeValues}`}
                                                    value={this._docTypeFilter}
                                                    onTiChange={ev => this._docTypeFilterChanged(ev.detail)}
                                                >
                                                </ti-select>
                                            }
                                            {index === this._titleColumnIndex &&
                                                <ti-search-field
                                                    placeholder={this.searchPlaceholder}
                                                    value={this._titleFilter}
                                                    onTiChange={ev => this._titleFilterChanged(ev.detail)}
                                                >
                                                </ti-search-field>
                                            }
                                        </div>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {this.items.map(item => <tr>
                                {this._headerKeyNames.map(column =>
                                    this._renderRowItem(item[column], column)
                                )}
                            </tr>)}
                        </tbody>
                    </table>
                    <span class="ti-techdocs-error">
                        <slot name="error-message" />
                    </span>
                    <button class="ti-techdocs-results-button" type="button" onClick={() => this._showAllParts()}>
                        <slot name="show-all-label" />
                        <svg class="ti-techdocs-results-button-icon" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" width="18" height="18" preserveAspectRatio="xMidYMid meet" viewBox="0 0 34 34">
                            <path d="M16.003 18.626l7.081-7.081L25 13.46l-8.997 8.998-9.003-9 1.917-1.916z" />
                        </svg>
                    </button>
                </Host>
            );
        } else {
            // if all fails, just show what was slotted
            return (<div><slot /></div>);
        }
    }
}
